import { haversineMeters } from './geo';
import { comparePk, sortByPk, type PkLike } from './pkCompare';
import type { PkMeshVertex } from './pkFromMesh';

export interface MeshPoint {
  lat: number;
  lng: number;
}

/**
 * Dónde cae un PK en el mapa. Es el **inverso** de `pkFromMesh`, sobre la misma
 * malla precacheada y sin tocar la red.
 *
 * ── Por qué existe ─────────────────────────────────────────────────────────
 *
 * Una actuación puntual (`pkInit === pkEnd`) se dibujaba como una línea gruesa
 * de más de medio kilómetro: `drawingWindow` ensancha ±300 m para que el
 * servidor devuelva vértices con los que trazar algo, y esa ventana de DIBUJO
 * acababa pintada tal cual. La actuación está en un punto, así que lo que hay
 * que pintar es un punto — y para eso hace falta saber sus coordenadas, no un
 * trozo de carretera alrededor.
 *
 * ── La interpolación ───────────────────────────────────────────────────────
 *
 * 🔴 Entre dos vértices NO se reparte restando etiquetas de PK: es el mismo
 * error del kilómetro largo que ya corrigió `interpolarPk`, del que esto es el
 * espejo. Con `84+1800` y `85+000` (90 m de asfalto en la C-17), un
 * `(pk.meter − a.meter) / (b.meter − a.meter)` da **−0,025** y el punto sale
 * detrás del primer vértice. Cuando los dos vértices son de kilómetros
 * distintos se cuenta **un metro de PK por metro de asfalto**, midiendo sobre
 * la geometría. Dentro del mismo kilómetro los metros sí son comparables y se
 * interpolan como siempre.
 *
 * ── Límites aceptados ──────────────────────────────────────────────────────
 *
 * - Fuera del rango de la malla devuelve `null`: un punto inventado es peor que
 *   ninguno (misma doctrina que el umbral de `pkFromMesh`).
 * - Resolución la de la malla, y la calzada la canónica (`getCachedRoadMesh`).
 *   Las dos calzadas están a 18,2 m medidos, irrelevante al zoom del mapa —
 *   para DIBUJAR es la lectura correcta. Ver `.claude/rules/malla-calzadas.md`.
 */
export function coordsFromPk(
  pk: PkLike,
  mesh: PkMeshVertex[]
): MeshPoint | null {
  if (mesh.length === 0) return null;

  const vertices = sortByPk(
    mesh.map((vertex) => ({
      km: vertex.pkKm,
      meter: vertex.pkMeter,
      lat: vertex.latitude,
      lng: vertex.longitude,
    }))
  );

  const primero = vertices[0];
  const ultimo = vertices[vertices.length - 1];
  if (comparePk(pk, primero) < 0 || comparePk(pk, ultimo) > 0) return null;

  // El último vértice que va antes (o justo en) el PK pedido.
  let i = 0;
  for (let j = 1; j < vertices.length; j += 1) {
    if (comparePk(vertices[j], pk) <= 0) i = j;
    else break;
  }

  const a = vertices[i];
  if (comparePk(a, pk) === 0) return { lat: a.lat, lng: a.lng };

  const b = vertices[i + 1];
  if (!b) return { lat: a.lat, lng: a.lng };

  const t =
    a.km === b.km
      ? fraccion(pk.meter - a.meter, b.meter - a.meter)
      : fraccion(
          pk.meter - a.meter,
          haversineMeters(a.lat, a.lng, b.lat, b.lng)
        );

  return {
    lat: a.lat + (b.lat - a.lat) * t,
    lng: a.lng + (b.lng - a.lng) * t,
  };
}

/** Fracción acotada a [0, 1]; con denominador nulo se queda en el vértice `a`. */
function fraccion(recorrido: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(1, Math.max(0, recorrido / total));
}

export default coordsFromPk;
